/**
 * App-level Sentry error boundary. Wraps the entire React tree so a
 * single component crash doesn't blank the page — users see a friendly
 * "Something went wrong" UI with a retry button, and Sentry receives
 * the captured exception with full component stack.
 *
 * When Sentry isn't enabled (preview / no DSN), Sentry.ErrorBoundary
 * still catches errors and shows the fallback UI — it just doesn't
 * forward them anywhere.
 */
import { SentryBoundary } from '@/lib/sentry';
import { Button } from '@/components/ui/button';

function Fallback({ resetError }) {
  return (
    <div
      className="min-h-screen flex items-center justify-center px-4"
      data-testid="sentry-error-fallback"
    >
      <div className="max-w-md text-center space-y-4">
        <h1 className="text-2xl font-bold">Something went wrong</h1>
        <p className="text-muted-foreground text-sm">
          We&apos;ve been notified and are looking into it. You can try refreshing
          the page or head back to the home screen.
        </p>
        <div className="flex items-center justify-center gap-3 pt-2">
          <Button
            variant="outline"
            onClick={resetError}
            data-testid="sentry-error-retry"
          >
            Try again
          </Button>
          <Button
            onClick={() => { window.location.href = '/'; }}
            data-testid="sentry-error-home"
          >
            Go home
          </Button>
        </div>
      </div>
    </div>
  );
}

export function SentryErrorBoundary({ children }) {
  return (
    <SentryBoundary fallback={Fallback}>
      {children}
    </SentryBoundary>
  );
}
